import React, { useState, useEffect } from 'react';
import api from '../api';
import { TrendingUp, Zap } from 'lucide-react';

/**
 * StudentXPBar — compact XP + level widget for the Student dashboard.
 * Auto-refreshes when `refreshKey` changes (bump after class completion).
 */
export const StudentXPBar = ({ refreshKey = 0, vertical = '' }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = vertical ? `/student/xp?vertical=${encodeURIComponent(vertical)}` : '/student/xp';
        const res = await api.get(path);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey, vertical]);

  if (!data) return null;
  const level = data.level ?? 1;
  const total = data.total_xp ?? 0;
  const next = data.next_level_at ?? 200;
  // Progress towards next level from previous threshold
  const thresholds = [0, 200, 500, 1000, 1750, 2750, 4000, 5500, 7500, 10000];
  const prev = thresholds[level - 1] ?? 0;
  const span = Math.max(1, next - prev);
  const pct = Math.min(100, Math.round(((total - prev) / span) * 100));

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-4"
      data-testid="student-xp-bar"
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-extrabold text-lg flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}
      >
        L{level}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="text-xs font-extrabold uppercase tracking-wider text-[#334155] flex items-center gap-1">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            Level {level}
          </span>
          <span className="text-xs font-bold text-[#334155]">
            <span className="text-[#0F172A] font-extrabold">{total.toLocaleString()}</span>
            <span className="text-[#64748B]"> / {next.toLocaleString()} XP</span>
          </span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #8B5CF6, #EC4899)' }}
          />
        </div>
        <p className="text-[10px] font-bold text-[#64748B] mt-1 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          {next - total > 0 ? `${(next - total).toLocaleString()} XP to Level ${level + 1}` : 'Max level reached — keep learning!'}
        </p>
      </div>
    </div>
  );
};

export default StudentXPBar;
